import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { KeyBrowser } from '@/components/inspect/KeyBrowser';
import { KeyValue } from '@/components/inspect/KeyValue';
import { TargetCard } from '@/components/inspect/TargetCard';
import { EmptyState, ErrorPage, LoadingPage, Stat } from '@/components/primitives';
import { useHasRole } from '@/hooks/useHasRole';
import { useRedisOverview } from '@/hooks/useSturmData';
import { bytes, count, percent, text } from '@/lib/inspectFormat';

type Tab = 'server' | 'databases' | 'keys';

export function RedisOverviewPage() {
  const { data, isLoading, isError, error } = useRedisOverview();
  const [tab, setTab] = useState<Tab>('server');
  const isAdmin = useHasRole('admin');

  if (isLoading) {
    return <LoadingPage label="Reading Redis…" />;
  }

  if (isError || !data) {
    return (
      <ErrorPage
        title="Could not load the Redis overview"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
      />
    );
  }

  const server = data.server;
  const totalKeys = data.databases.reduce((total, db) => total + db.keys, 0);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Redis overview</div>
          <div className="page-sub">
            Sturm does not use Redis — its SignalR backplane is the Postgres outbox. This page
            observes the shared MedicalInsider cache read-only.
          </div>
        </div>
      </div>

      <TargetCard target={data.target} icon="database">
        <div className="kv" style={{ minWidth: 240 }}>
          <KeyValue k="Default database" v={data.database} />
          {data.keyPrefix ? <KeyValue k="Key prefix" v={data.keyPrefix} mono /> : null}
        </div>
      </TargetCard>

      <div className="grid-4 mb-24">
        <Stat icon="hash" label="Keys · all databases" value={count(totalKeys)} />
        <Stat
          icon="server"
          label="Used memory"
          value={bytes(server?.usedMemory)}
          meta={server ? `peak ${bytes(server.usedMemoryPeak)}` : undefined}
        />
        <Stat icon="bolt" label="Ops/sec" value={count(server?.opsPerSecond)} />
        <Stat
          icon="target"
          label="Hit rate"
          value={percent(server?.hitRate)}
          meta={
            server ? `${count(server.keyspaceHits)} hits / ${count(server.keyspaceMisses)} misses` : undefined
          }
        />
      </div>

      {server == null ? (
        <div className="card">
          <EmptyState
            title={`${data.target.label} is ${data.target.status === 'unconfigured' ? 'not configured' : 'unreachable'}`}
            sub={data.target.error ?? 'No server information is available.'}
          />
        </div>
      ) : (
        <>
          <div className="tabs">
            <div className={`tab ${tab === 'server' ? 'active' : ''}`} onClick={() => setTab('server')}>
              <Icon name="server" size={14} /> Server
            </div>
            <div className={`tab ${tab === 'databases' ? 'active' : ''}`} onClick={() => setTab('databases')}>
              <Icon name="layers" size={14} /> Databases{' '}
              <span className="count">{data.databases.length}</span>
            </div>
            <div className={`tab ${tab === 'keys' ? 'active' : ''}`} onClick={() => setTab('keys')}>
              <Icon name="search" size={14} /> Keys
            </div>
          </div>

          {tab === 'server' ? (
            <div className="grid-2">
              <div className="card">
                <div className="card-hd">Instance</div>
                <div className="card-pad kv">
                  <KeyValue k="Version" v={server.version} />
                  <KeyValue k="Mode" v={server.mode} />
                  <KeyValue k="Role" v={server.role} />
                  <KeyValue k="Uptime" v={server.uptime} />
                  <KeyValue k="Clients" v={count(server.connectedClients)} />
                  <KeyValue k="Blocked" v={count(server.blockedClients)} />
                  <KeyValue k="Connections" v={count(server.totalConnectionsReceived)} />
                </div>
              </div>
              <div className="card">
                <div className="card-hd">Memory and throughput</div>
                <div className="card-pad kv">
                  <KeyValue k="Used" v={bytes(server.usedMemory)} />
                  <KeyValue k="Peak" v={bytes(server.usedMemoryPeak)} />
                  <KeyValue k="RSS" v={bytes(server.usedMemoryRss)} />
                  <KeyValue k="Fragmentation" v={server.fragmentationRatio.toFixed(2)} />
                  <KeyValue
                    k="Max memory"
                    v={server.maxMemory == null ? 'unlimited' : bytes(server.maxMemory)}
                  />
                  <KeyValue k="Eviction" v={text(server.maxMemoryPolicy)} />
                  <KeyValue k="Commands" v={count(server.totalCommands)} />
                  <KeyValue k="Evicted" v={count(server.evictedKeys)} />
                  <KeyValue k="Expired" v={count(server.expiredKeys)} />
                </div>
              </div>
            </div>
          ) : null}

          {tab === 'databases' ? (
            <div className="card">
              {data.databases.length === 0 ? (
                <EmptyState title="No populated databases" sub="Every database on this instance is empty." />
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Database</th>
                      <th>Keys</th>
                      <th>With TTL</th>
                      <th>Average TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.databases.map((db) => (
                      <tr key={db.index}>
                        <td>
                          <span className="tbl-strong mono">db {db.index}</span>
                          {db.isDefault ? <span className="tbl-sub">configured default</span> : null}
                        </td>
                        <td className="tbl-num">{count(db.keys)}</td>
                        <td className="tbl-num">{count(db.expires)}</td>
                        <td className="tbl-num">{count(db.avgTtl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}

          {tab === 'keys' ? (
            isAdmin ? (
              <KeyBrowser
                keyPrefix={data.keyPrefix}
                defaultDatabase={data.database}
                databases={data.databases}
              />
            ) : (
              <div className="card">
                <EmptyState
                  title="Browsing keys requires the Admin role"
                  sub="Key names on the shared cache can identify individual records, so they are held to a higher floor than the counters above."
                />
              </div>
            )
          ) : null}
        </>
      )}
    </div>
  );
}
